import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useStores } from "@/lib/stores";
import { logError } from "@/lib/logger";
import { Wrench, LayoutDashboard, ClipboardList, CreditCard, Plus, ShieldCheck, LogOut, TrendingUp, Store, Check, KanbanSquare, Settings, Mail, Menu, X, Ban, Truck, PieChart, Plug } from "lucide-react";

const BADGE_KEY = { "/admin/kanban": "kanban_new", "/admin/pendentes": "pending", "/admin/cancelamentos": "cancellations", "/admin/envios": "shipments" };

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { stores, activeStore, pick } = useStores();
  const nav = useNavigate();
  const loc = useLocation();
  const isSuper = user?.role === "superadmin";
  const isCEO = isSuper && Array.isArray(user?.tags) && user.tags.includes("ceo");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [counts, setCounts] = useState({ kanban_new: 0, pending: 0, cancellations: 0, shipments: 0 });

  // Fechar menu ao navegar (mobile)
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Poll notification counts every 5s
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const mod = await import("@/lib/api");
        const { data } = await mod.api.get("/admin/notifications/counts");
        if (!cancelled) setCounts(data);
      } catch (err) {
        // Sidebar counter is decoration — 401 during session churn is expected, log the rest.
        if (err?.response?.status !== 401) logError("[nav-counters] tick:", err);
      }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, activeStore]);

  const items = [
    { to: "/admin", icon: LayoutDashboard, label: "Visão geral", end: true },
    { to: "/admin/kanban", icon: KanbanSquare, label: "Fila do Técnico" },
    { to: "/admin/pendentes", icon: ClipboardList, label: "Serviços Pendentes" },
    { to: "/admin/pagamentos", icon: CreditCard, label: "Pagamentos Pendentes" },
    { to: "/admin/nova-os", icon: Plus, label: "Nova OS" },
    { to: "/admin/rankings", icon: TrendingUp, label: "Ranking de Peças" },
    { to: "/admin/cancelamentos", icon: Ban, label: "Cancelamentos" },
    { to: "/admin/envios", icon: Truck, label: "Solicitações de Envio" },
    ...(isSuper ? [{ to: "/admin/lojas", icon: Store, label: "Lojas" }] : []),
    ...(isCEO ? [{ to: "/admin/relatorios/split", icon: PieChart, label: "Relatório de Split" }] : []),
    ...(isCEO ? [{ to: "/admin/integracoes", icon: Plug, label: "Integrações" }] : []),
    ...(isSuper ? [{ to: "/admin/emails", icon: Mail, label: "E-mails" }] : []),
    ...(isSuper ? [{ to: "/admin/configuracoes", icon: Settings, label: "Configurações" }] : []),
    { to: "/admin/auditoria", icon: ShieldCheck, label: "Audit Logs" },
  ];

  const currentStoreObj = stores.find((s) => s.store_id === (isSuper ? activeStore : user?.store_id));

  const SidebarContent = (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-white text-neutral-900 grid place-items-center rounded-lg"><Wrench size={18} /></div>
          <div className="font-heading font-black text-lg text-white">GTec</div>
        </div>
        <button onClick={() => setMobileOpen(false)} className="lg:hidden text-neutral-400 hover:text-white p-1" aria-label="Fechar menu">
          <X size={20} />
        </button>
      </div>

      {/* Store selector */}
      <div className="mb-6 pb-4 border-b border-neutral-800">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-2">Contexto de loja</div>
        {isSuper ? (
          <div className="space-y-1 max-h-52 overflow-y-auto">
            <button
              onClick={() => pick("")}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center justify-between ${!activeStore ? "bg-white text-neutral-900 font-semibold" : "text-neutral-400 hover:text-white hover:bg-neutral-800"}`}
              data-testid="store-picker-all"
            >
              <span>Todas as lojas</span>
              {!activeStore && <Check size={12}/>}
            </button>
            {stores.map((s) => (
              <button
                key={s.store_id}
                onClick={() => pick(s.store_id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs flex items-center justify-between ${activeStore === s.store_id ? "bg-white text-neutral-900 font-semibold" : "text-neutral-400 hover:text-white hover:bg-neutral-800"}`}
                data-testid={`store-picker-${s.code}`}
              >
                <span className="truncate">{s.name} <span className="font-mono opacity-60">{s.code}</span></span>
                {activeStore === s.store_id && <Check size={12}/>}
              </button>
            ))}
          </div>
        ) : (
          <div className="px-3 py-2 rounded-lg bg-neutral-800 text-white text-xs">
            <div className="font-semibold">{currentStoreObj?.name || "—"}</div>
            <div className="font-mono text-[10px] text-neutral-400 mt-0.5">{currentStoreObj?.code}-XXXXX</div>
          </div>
        )}
      </div>

      <nav className="space-y-1 flex-1 overflow-y-auto">
        {items.map((it) => {
          const badgeKey = BADGE_KEY[it.to];
          const count = badgeKey ? (counts[badgeKey] || 0) : 0;
          return (
          <NavLink
            key={it.to} to={it.to} end={it.end}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm relative ${isActive ? "bg-white text-neutral-900 font-semibold" : "text-neutral-400 hover:text-white hover:bg-neutral-800"}`}
            data-testid={`nav-${it.to.replace("/admin", "admin").replace(/\//g, "-")}`}
          >
            <it.icon size={16} /> <span className="flex-1">{it.label}</span>
            {count > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`badge-${badgeKey}`}>
                {count > 99 ? "99+" : count}
              </span>
            )}
          </NavLink>
          );
        })}
      </nav>
      <div className="pt-4 border-t border-neutral-800">
        <div className="text-xs text-neutral-500 mb-2 truncate">{user?.email}</div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-600 mb-2">{isSuper ? "Super-admin" : "Admin de loja"}</div>
        <button onClick={async () => { await logout(); nav("/login"); }} className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white" data-testid="admin-logout">
          <LogOut size={14} /> Sair
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-neutral-900 text-white flex items-center justify-between px-4 h-14 border-b border-neutral-800">
        <button onClick={() => setMobileOpen(true)} className="p-1 -ml-1" aria-label="Abrir menu" data-testid="mobile-menu-toggle">
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-white text-neutral-900 grid place-items-center rounded"><Wrench size={14} /></div>
          <span className="font-heading font-black">GTec</span>
        </div>
        <div className="w-6" />
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-64 min-h-screen bg-neutral-900 text-neutral-200 flex-col p-5 sticky top-0">
          {SidebarContent}
        </aside>

        {/* Mobile sidebar overlay */}
        {mobileOpen && (
          <>
            <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-backdrop" />
            <aside className="fixed inset-y-0 left-0 z-50 w-72 bg-neutral-900 text-neutral-200 flex flex-col p-5 lg:hidden animate-in slide-in-from-left duration-200" data-testid="mobile-sidebar">
              {SidebarContent}
            </aside>
          </>
        )}

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
